<script lang="ts">
	import {
		Button,
		Col,
		Input,
		Row,
		Text,
		Tooltip,
		Icon,
		Select,
		Toggle,
	} from '@Shelly/shelly-design-system/index';
	import { createEventDispatcher } from 'svelte';
	import { get } from 'svelte/store';
	import { format } from 'svelte-i18n';
	import {
		validateZoneInput,
		validateZoneDetectionHeights,
		formatZoneDetectionHeightErrors,
		ZONE_COLOR_PALETTE,
		DETECTION_HEIGHT_MAX_M,
		maxDetectionHeightInputMinBound,
		minDetectionHeightInputMaxBound,
	} from '../../helpers/radar-helper';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { toast } from '@Shelly/shelly-design-system/index';

	const dispatch = createEventDispatcher();

	export let settings: any = [];
	export let isMainZone: boolean = false;
	export let zoneId: number;
	export let device: any;
	/** When false the built-in "Save" button is hidden; the parent renders its own action row. */
	export let showSaveButton: boolean = true;
	/** Bindable – true whenever the user has made changes that differ from the loaded values. */
	export let hasChanges: boolean = false;

	// Get the zone key and settings
	$: zoneKey = `presencezone:${zoneId}`;
	$: zoneSettings = settings[zoneKey] || {};

	// Local state variables for form inputs
	let zoneName = 'Room';
	let zoneEnabled = true;
	let zoneColor = [65, 209, 180];
	let zoneMinHeight = '0';
	let zoneMaxHeight = '3';
	let zonePresenceThreshold = '0';
	let zoneAbsenceThreshold = '0';
	let initialized = false;

	// Baseline snapshots for change detection
	let initialName = zoneName;
	let initialEnabled = zoneEnabled;
	let initialColor = [...zoneColor];
	let initialMinHeight = zoneMinHeight;
	let initialMaxHeight = zoneMaxHeight;
	let initialPresenceThreshold = zonePresenceThreshold;
	let initialAbsenceThreshold = zoneAbsenceThreshold;

	// Validation error states
	let minHeightError = '';
	let maxHeightError = '';
	let presenceThresholdError = '';
	let absenceThresholdError = '';

	$: parsedZoneMax = parseFloat(zoneMaxHeight);
	$: parsedZoneMin = parseFloat(zoneMinHeight);
	$: zoneMinHeightInputMax = minDetectionHeightInputMaxBound(parsedZoneMax);
	$: zoneMaxHeightInputMin = maxDetectionHeightInputMinBound(parsedZoneMin);

	function validatePresenceThreshold() {
		const result = validateZoneInput(zonePresenceThreshold, 'PRESENCE_THRESHOLD');
		presenceThresholdError = result.isValid ? '' : result.error || '';
	}

	function validateAbsenceThreshold() {
		const result = validateZoneInput(zoneAbsenceThreshold, 'ABSENCE_THRESHOLD');
		absenceThresholdError = result.isValid ? '' : result.error || '';
	}

	function validateHeights() {
		const raw = validateZoneDetectionHeights(zoneMinHeight, zoneMaxHeight);
		const { minError, maxError } = formatZoneDetectionHeightErrors(raw, get(format));
		minHeightError = minError;
		maxHeightError = maxError;
	}

	// Check if form is valid for saving
	$: isFormValid =
		!minHeightError && !maxHeightError && !presenceThresholdError && !absenceThresholdError;

	$: radarPositionOptions = [
		{ title: $format('presence_radar_position_center', { default: 'Center' }), value: 'center' },
		{ title: $format('presence_radar_position_left', { default: 'Left' }), value: 'left' },
		{ title: $format('presence_radar_position_right', { default: 'Right' }), value: 'right' },
	];

	$: selectedRadarPosition = radarPositionOptions.find(
		(option) => option.value === (settings?.presence?.sensor?.position ?? 'center'),
	)?.title;

	// Reset initialization when zoneId changes
	$: if (zoneId) {
		initialized = false;
	}

	// Update local state when zone settings change (only on initial load)
	$: if (zoneSettings && Object.keys(zoneSettings).length > 0 && !initialized) {
		zoneName = zoneSettings.name || 'Room';
		zoneEnabled = zoneSettings.enable !== false;
		zoneColor = zoneSettings.color || [65, 209, 180];
		zoneMinHeight =
			zoneSettings.zmin != null && zoneSettings.zmin !== '' ? String(zoneSettings.zmin) : '0';
		zoneMaxHeight =
			zoneSettings.zmax != null && zoneSettings.zmax !== '' ? String(zoneSettings.zmax) : '3';
		zonePresenceThreshold = String(zoneSettings.presence_thr ?? 0);
		zoneAbsenceThreshold = String(zoneSettings.absence_thr ?? 0);

		// Snapshot baseline for change detection
		initialName = zoneName;
		initialEnabled = zoneEnabled;
		initialColor = [...zoneColor];
		initialMinHeight = zoneMinHeight;
		initialMaxHeight = zoneMaxHeight;
		initialPresenceThreshold = zonePresenceThreshold;
		initialAbsenceThreshold = zoneAbsenceThreshold;

		initialized = true;
		validateHeights();
		validatePresenceThreshold();
		validateAbsenceThreshold();
	}

	$: hasChanges =
		initialized &&
		(zoneName !== initialName ||
			zoneEnabled !== initialEnabled ||
			JSON.stringify(zoneColor) !== JSON.stringify(initialColor) ||
			String(zoneMinHeight) !== String(initialMinHeight) ||
			String(zoneMaxHeight) !== String(initialMaxHeight) ||
			String(zonePresenceThreshold) !== String(initialPresenceThreshold) ||
			String(zoneAbsenceThreshold) !== String(initialAbsenceThreshold));

	/** Resets local form state back to the last-saved baseline (called by parent on Discard). */
	export function resetChanges() {
		zoneName = initialName;
		zoneEnabled = initialEnabled;
		zoneColor = [...initialColor];
		zoneMinHeight = initialMinHeight;
		zoneMaxHeight = initialMaxHeight;
		zonePresenceThreshold = initialPresenceThreshold;
		zoneAbsenceThreshold = initialAbsenceThreshold;
		validateHeights();
		validatePresenceThreshold();
		validateAbsenceThreshold();
	}

	/** Syncs the dirty baseline to current values after a successful save (clears Save/Discard). */
	export function acknowledgeSaved() {
		initialName = zoneName;
		initialEnabled = zoneEnabled;
		initialColor = [...zoneColor];
		initialMinHeight = String(zoneMinHeight);
		initialMaxHeight = String(zoneMaxHeight);
		initialPresenceThreshold = String(zonePresenceThreshold);
		initialAbsenceThreshold = String(zoneAbsenceThreshold);
	}

	// Convert RGB array to hex for color picker
	$: colorHex = `#${zoneColor.map((c) => c.toString(16).padStart(2, '0')).join('')}`;

	// Convert RGB array to CSS-compatible string
	function rgbToCss(rgb: readonly number[]): string {
		return `rgb(${rgb.join(',')})`;
	}

	// Predefined color palette (from radar-helper constants)
	let colors = ZONE_COLOR_PALETTE;

	// Select a color swatch
	function selectColor(color) {
		zoneColor = color;
	}

	export async function handleConfigSave() {
		validateHeights();
		validatePresenceThreshold();
		validateAbsenceThreshold();

		// Don't save if there are validation errors
		if (!isFormValid) {
			return;
		}

		const config = {
			name: zoneName,
			enable: zoneEnabled,
			color: zoneColor,
			area: zoneSettings.area || [], // Keep existing area
			zmin: Number(zoneMinHeight),
			zmax: Number(zoneMaxHeight),
			presence_thr: Number(zonePresenceThreshold),
			absence_thr: Number(zoneAbsenceThreshold),
			meta: zoneSettings.meta || {},
		};

		// Dispatch the configuration to parent
		dispatch('configSave', { zoneId, config });
	}

	async function handleRadarPositionChange(event) {
		const newPosition = event.detail.value;

		try {
			// Save radar position setting
			const result = await gen2Request(device, 'presence.setconfig', {
				config: {
					sensor: {
						position: newPosition,
					},
				},
			});

			if (result.isok) {
				toast.open({
					type: 'success',
					title: $format('presence_toast_radar_position_saved', {
						default: 'Radar position saved successfully.',
					}),
				});
			} else {
				toast.open({
					type: 'danger',
					title: $format('presence_toast_radar_position_failed', {
						default: 'Failed to save radar position.',
					}),
				});
			}
		} catch (error) {
			console.error('Error saving radar position:', error);
			toast.open({
				type: 'danger',
				title: $format('presence_toast_radar_position_failed', {
					default: 'Failed to save radar position.',
				}),
			});
		}
	}
</script>

<div class="zone-config-inputs">
	<Row>
		<Col sm="12" md="12" lg="12">
			<div class="headline-with-info">
				<p role="presentation" class="body-styles">
					{$format('presence_zone_name_label', { default: "Zone's name" })}
				</p>
				<Tooltip
					placement="top"
					title={isMainZone
						? $format('presence_zone_name_tooltip_default', {
								default: "Default zone's name cannot be changed",
							})
						: $format('presence_zone_name_tooltip', {
								default:
									'Set a custom name for this detection zone to easily identify it in your radar system.',
							})}
				>
					<Icon icon="fas fa-info-circle" size="xs" class="text-color-muted ml-xs" />
				</Tooltip>
			</div>
			<Input
				id="zone-name-{zoneId}"
				type="text"
				bind:value={zoneName}
				aria-label={$format('label_room_area_name', { default: 'Room area name' })}
				disabled={isMainZone}
				placeholder={$format('presence_zone_name_placeholder', { default: 'Enter zone name' })}
			/>
		</Col>
	</Row>

	<Row>
		<Col sm="12" md="12" lg="12" class="mt-l">
			<div class="headline-with-info">
				<Text styleType="body" class="mb-xs">
					{$format('presence_config_min_height_label', {
						default: 'Min detection height (m)',
					})}
				</Text>
				<Tooltip
					placement="top"
					title={$format('presence_config_min_height_tooltip', {
						default:
							'Set the minimum height above the floor at which presence can be detected. Allowed range: 0 m up to (max detection height − 0.1 m).',
					})}
				>
					<Icon icon="fas fa-info-circle" size="xs" class="text-color-muted ml-xs" />
				</Tooltip>
			</div>
			<Text styleType="body-small" class="text-color-muted mb-s">
				{$format('presence_config_min_height_tooltip', {
					default:
						'Set the minimum height above the floor at which presence can be detected. Allowed range: 0 m up to (max detection height − 0.1 m).',
				})}
			</Text>
			<Input
				id="zone-min-height-{zoneId}"
				type="number"
				min="0"
				max={zoneMinHeightInputMax}
				step="0.01"
				class="text-left"
				bind:value={zoneMinHeight}
				label={$format('presence_config_min_height_label', {
					default: 'Min detection height (m)',
				})}
				aria-label={$format('presence_config_min_height_label', {
					default: 'Min detection height (m)',
				})}
				on:blur={validateHeights}
			/>
			{#if minHeightError}
				<Text styleType="body-small" class="text-color-danger mt-xs">{minHeightError}</Text>
			{/if}
		</Col>
	</Row>

	<Row>
		<Col sm="12" md="12" lg="12" class="mt-l">
			<div class="headline-with-info">
				<Text styleType="body" class="mb-xs">
					{$format('presence_config_max_height_label', {
						default: 'Max detection height (m)',
					})}
				</Text>
				<Tooltip
					placement="top"
					title={$format('presence_zone_max_height_tooltip', {
						default:
							'Set the maximum height above the floor at which motion can be detected. Allowed range: (min detection height + 0.1 m) up to 5 m.',
					})}
				>
					<Icon icon="fas fa-info-circle" size="xs" class="text-color-muted ml-xs" />
				</Tooltip>
			</div>
			<Text styleType="body-small" class="text-color-muted mb-s">
				{$format('presence_zone_max_height_tooltip', {
					default:
						'Set the maximum height above the floor at which motion can be detected. Allowed range: (min detection height + 0.1 m) up to 5 m.',
				})}
			</Text>
			<Input
				id="zone-max-height-{zoneId}"
				type="number"
				min={zoneMaxHeightInputMin}
				max={DETECTION_HEIGHT_MAX_M}
				step="0.01"
				class="text-left"
				bind:value={zoneMaxHeight}
				label={$format('presence_config_max_height_label', {
					default: 'Max detection height (m)',
				})}
				aria-label={$format('presence_config_max_height_label', {
					default: 'Max detection height (m)',
				})}
				on:blur={validateHeights}
			/>
			{#if maxHeightError}
				<Text styleType="body-small" class="text-color-danger mt-xs">{maxHeightError}</Text>
			{/if}
		</Col>
	</Row>

	<Row>
		<Col sm="12" md="12" lg="12" class="mt-l">
			<div class="headline-with-info">
				<Text styleType="body" class="mb-xs">
					{$format('presence_zone_absence_threshold_label', {
						default: 'Absence threshold (s)',
					})}
				</Text>
				<Tooltip
					placement="top"
					title={$format('presence_zone_absence_threshold_tooltip', {
						default:
							'Set how long no presence must be detected before the room area is marked as empty. Range: 0 to 3600 s.',
					})}
				>
					<Icon icon="fas fa-info-circle" size="xs" class="text-color-muted ml-xs" />
				</Tooltip>
			</div>
			<Text styleType="body-small" class="text-color-muted mb-s">
				{$format('presence_zone_absence_threshold_tooltip', {
					default:
						'Set how long no presence must be detected before the room area is marked as empty. Range: 0 to 3600 s.',
				})}
			</Text>
			<Input
				id="zone-absence-thr-{zoneId}"
				type="number"
				min="0"
				max="3600"
				step="1"
				class="text-left"
				bind:value={zoneAbsenceThreshold}
				label={$format('presence_zone_absence_threshold_label', {
					default: 'Absence threshold (s)',
				})}
				aria-label={$format('presence_zone_absence_threshold_label', {
					default: 'Absence threshold (s)',
				})}
				on:blur={validateAbsenceThreshold}
			/>
			{#if absenceThresholdError}
				<Text styleType="body-small" class="text-color-danger mt-xs"
					>{absenceThresholdError}</Text
				>
			{/if}
		</Col>
	</Row>

	<Row>
		<Col sm="12" md="12" lg="12" class="mt-l">
			<div class="headline-with-info">
				<Text styleType="body" class="mb-xs">
					{$format('presence_zone_presence_threshold_label', {
						default: 'Presence threshold (s)',
					})}
				</Text>
				<Tooltip
					placement="top"
					title={$format('presence_zone_presence_threshold_tooltip', {
						default:
							'Set how long presence must be detected before the room area is marked as occupied. Range: 0 to 3600 s.',
					})}
				>
					<Icon icon="fas fa-info-circle" size="xs" class="text-color-muted ml-xs" />
				</Tooltip>
			</div>
			<Text styleType="body-small" class="text-color-muted mb-s">
				{$format('presence_zone_presence_threshold_tooltip', {
					default:
						'Set how long presence must be detected before the room area is marked as occupied. Range: 0 to 3600 s.',
				})}
			</Text>
			<Input
				id="zone-presence-thr-{zoneId}"
				type="number"
				min="0"
				max="3600"
				step="1"
				class="text-left"
				bind:value={zonePresenceThreshold}
				label={$format('presence_zone_presence_threshold_label', {
					default: 'Presence threshold (s)',
				})}
				aria-label={$format('presence_zone_presence_threshold_label', {
					default: 'Presence threshold (s)',
				})}
				on:blur={validatePresenceThreshold}
			/>
			{#if presenceThresholdError}
				<Text styleType="body-small" class="text-color-danger mt-xs"
					>{presenceThresholdError}</Text
				>
			{/if}
		</Col>
	</Row>

	{#if !isMainZone}
		<Row>
			<Col sm="12" md="12" lg="12" class="mt-l">
				<div class="d-flex align-items-center justify-content-between">
					<Text styleType="body">
						{$format('presence_zone_enabled_label', { default: 'Zone enabled' })}
					</Text>
					<Toggle
						id="zone-enabled-{zoneId}"
						bind:checked={zoneEnabled}
						aria-label={$format('presence_zone_enabled_label', { default: 'Zone enabled' })}
					/>
				</div>
			</Col>
		</Row>
	{/if}

	<Row>
		<Col sm="12" md="12" lg="12" class="mt-xl">
			<div class="headline-with-info">
				<Text styleType="body" class="mb-xs">
					{$format('label_select_room_area_color', { default: 'Select room area color' })}
				</Text>
				<Tooltip
					placement="top"
					title="Choose a distinctive color for this zone to easily identify it on the radar visualization. This color will be consistent across all views."
				>
					<Icon icon="fas fa-info-circle" size="xs" class="text-color-muted ml-xs" />
				</Tooltip>
			</div>
			<div class="color-picker">
				{#each colors as color}
					<button
						class="swatch {JSON.stringify(zoneColor) === JSON.stringify(color)
							? 'selected'
							: ''}"
						style="background-color: {rgbToCss(color)}"
						on:click={() => selectColor(color)}
						aria-label={rgbToCss(color)}
					></button>
				{/each}
			</div>
		</Col>
	</Row>

	{#if isMainZone}
		<!-- Radar Position Setting (only for main zone) -->
		<Row>
			<Col sm="12" md="12" lg="12" class="mt-l">
				<div class="headline-with-info">
					<Text styleType="body" class="mb-xs">
						{$format('label_radar_position', { default: 'Radar position' })}
					</Text>
					<Tooltip
						placement="top"
						title="Select the physical position of your radar sensor relative to the room. This affects how coordinates are calculated and displayed on the radar visualization."
					>
						<Icon icon="fas fa-info-circle" size="xs" class="text-color-muted ml-xs" />
					</Tooltip>
				</div>
				<Select
					id="radar-position-select-{zoneId}"
					icon={null}
					placeholder={$format('presence_zone_radar_position_placeholder', {
						default: 'Select radar position',
					})}
					options={radarPositionOptions}
					bind:value={selectedRadarPosition}
					on:change={handleRadarPositionChange}
					aria-label={$format('label_radar_position', { default: 'Radar position' })}
				/>
			</Col>
		</Row>
	{/if}

	<hr class="mt-l mb-s" />

	{#if showSaveButton}
		<Row>
			<Col sm="12" md="12" lg="12" class="mt-m">
				<Button
					variant="custom"
					customSettings={{
						background: '#transparent',
						color: '#00000',
					}}
					size="m"
					style="font-weight: 400; border: 1px solid #393939"
					disabled={!isFormValid}
					on:click={handleConfigSave}
					>{$format('button_save_zone_config', { default: 'Save zone configuration' })}</Button
				>
			</Col>
		</Row>
	{/if}
</div>

<style>
	.zone-config-inputs {
		padding: 1rem 0;
		max-width: 500px; /* Limit the maximum width */
	}

	/* Add body-styles for consistency */
	.body-styles {
		color: #929293;
		font-size: 14px;
	}

	/* Headline with info icon styling */
	.headline-with-info {
		display: flex;
		align-content: space-between;
		align-items: baseline;
		gap: 4px;
		margin-bottom: 0.5rem;
	}

	.headline-with-info p {
		margin: 0;
	}

	/* Color picker styles (from EditZone) */
	.color-picker {
		display: flex;
		gap: 8px;
		align-items: center;
	}

	/* Swatches */
	.swatch {
		width: 25px;
		height: 25px;
		border-radius: 50%;
		border: 2px solid transparent;
		position: relative;
		cursor: pointer;
		transition: transform 0.1s;
	}

	.swatch:hover {
		transform: scale(1.1);
	}

	/* Selected state */
	.swatch.selected {
		box-shadow: inset 0 0 0 2px #453e3e;
	}

	.swatch.selected::after {
		content: '✓';
		font-size: 14px;
		color: #453e3e;
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
	}
</style>
